import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Badge, Button, Col, FloatingLabel, Form, Modal, ProgressBar, Row, Spinner, Table } from "react-bootstrap";

const today = () => moment().format("YYYY-MM-DD");
const formatDate = (date) => (date ? moment(date, "YYYY-MM-DD").format("D MMMM YYYY") : "-");
const captureVariant = (percent) => (percent >= 80 ? "success" : percent >= 50 ? "warning" : "danger");

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("AuthUser")}` });

const Stat = ({ label, value, color, hint, onClick }) => (
  <Col xs={6} md={4} xl={2} className="mb-3">
    <div
      className="border rounded p-3 h-100 bg-white"
      style={onClick ? { cursor: "pointer" } : undefined}
      onClick={onClick}
      title={onClick ? "Click for details" : undefined}
    >
      <div className="text-muted small">{label}</div>
      <h3 className="mb-0" style={{ color }}>{value}</h3>
      {hint && <div className="text-muted small">{hint}</div>}
    </div>
  </Col>
);

// Follow-up progress of a day (or date range): assigned, entered, pending and how much data was captured
export default function FollowupProgressCard() {
  const [filters, setFilters] = useState({ from_date: today(), to_date: today() });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Total Pending details: null = closed, "" = all types, otherwise a follow-up type id
  const [pendingType, setPendingType] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  useEffect(() => {
    if (!filters.from_date || !filters.to_date || filters.to_date < filters.from_date) return;
    setLoading(true);
    setError("");
    axios
      .get(`${process.env.REACT_APP_API_URL}/followup-report`, {
        params: filters,
        headers: authHeaders(),
      })
      .then((res) => setReport(res.data))
      .catch(() => setError("Could not load the follow-up report."))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    if (pendingType === null) return;
    setPendingLoading(true);
    setPendingRows([]);
    axios
      .get(`${process.env.REACT_APP_API_URL}/followup-report/pending`, {
        params: pendingType ? { type_id: pendingType } : {},
        headers: authHeaders(),
      })
      .then((res) => setPendingRows(res.data.pending || []))
      .catch(() => setPendingRows([]))
      .finally(() => setPendingLoading(false));
  }, [pendingType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "from_date" && next.to_date < value) next.to_date = value;
      return next;
    });
  };

  const summary = report?.summary;
  const rangeLabel = filters.from_date === filters.to_date
    ? formatDate(filters.from_date)
    : `${formatDate(filters.from_date)} - ${formatDate(filters.to_date)}`;
  const completedPercent = summary?.assigned ? Math.round((summary.completed * 100) / summary.assigned) : 0;

  return (
    <div className="mc-card mb-4">
      <div className="mc-card-header">
        <h4 className="mc-card-title">Follow Up Progress - {rangeLabel}</h4>
      </div>

      <Row className="mb-3 align-items-center">
        <Col md={3}>
          <FloatingLabel label="From Date">
            <Form.Control type="date" name="from_date" value={filters.from_date} onChange={handleChange} />
          </FloatingLabel>
        </Col>
        <Col md={3}>
          <FloatingLabel label="To Date">
            <Form.Control type="date" name="to_date" value={filters.to_date} min={filters.from_date} onChange={handleChange} />
          </FloatingLabel>
        </Col>
        <Col md={2}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setFilters({ from_date: today(), to_date: today() })}
          >
            Today
          </Button>
        </Col>
        <Col md={4} className="text-end">
          {loading && <Spinner animation="border" size="sm" variant="success" />}
        </Col>
      </Row>

      {error && <p className="text-danger">{error}</p>}

      {summary && (
        <>
          <Row>
            <Stat label="Assigned" value={summary.assigned} color="#0d6efd" hint="distributed in this period" />
            <Stat label="Entered" value={summary.entered} color="#077834" hint="follow-ups saved" />
            <Stat label="Completed" value={summary.completed} color="#198754" hint="of the assigned" />
            <Stat label="Pending" value={summary.pending} color="#dc3545" hint="of the assigned" />
            <Stat label="Total Pending" value={summary.total_pending} color="#fd7e14" hint="due by schedule till today - click for details" onClick={() => setPendingType("")} />
            <Stat label="Data Captured" value={`${summary.data_captured}%`} color="#6f42c1" hint="of form fields" />
          </Row>

          <div className="mb-3">
            <div className="d-flex justify-content-between small text-muted">
              <span>Assigned follow-ups completed</span>
              <span>{summary.completed} / {summary.assigned}</span>
            </div>
            <ProgressBar now={completedPercent} label={`${completedPercent}%`} variant="success" />
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3 small">
            <Badge bg="light" text="dark">Questions answered: {summary.questions_answered} / {summary.questions_total}</Badge>
            <Badge bg="light" text="dark">Photos: {summary.photos} / {summary.photos_total}</Badge>
            <Badge bg="light" text="dark">Video links: {summary.videos} / {summary.entered}</Badge>
            {report.by_type.map((row) => (
              <Badge key={row.type} bg="secondary">{row.type || "-"}: {row.entered}</Badge>
            ))}
          </div>

          {summary.total_pending > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3 small align-items-center">
              <span className="text-muted">Pending by schedule:</span>
              {report.overdue.filter((row) => row.pending > 0).map((row) => (
                <Badge
                  key={row.type_id}
                  bg="warning"
                  text="dark"
                  style={{ cursor: "pointer" }}
                  onClick={() => setPendingType(row.type_id)}
                >
                  {row.type}: {row.pending}
                </Badge>
              ))}
            </div>
          )}

          <h6>By User</h6>
          <Table bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>User</th>
                <th>Assigned</th>
                <th>Entered</th>
                <th>Pending</th>
                <th style={{ minWidth: 160 }}>Data Captured</th>
              </tr>
            </thead>
            <tbody>
              {report.users.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted">No follow-up activity in this period.</td></tr>
              ) : (
                report.users.map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.name}</td>
                    <td>{user.assigned}</td>
                    <td>{user.entered}</td>
                    <td><Badge bg={user.pending ? "warning" : "success"}>{user.pending}</Badge></td>
                    <td>
                      {user.entered ? (
                        <ProgressBar now={user.data_captured} label={`${user.data_captured}%`} variant={captureVariant(user.data_captured)} />
                      ) : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {report.followups.length > 0 && (
            <>
              <h6>Entered Follow-ups</h6>
              <Table bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>FSF ID</th>
                    <th>Family</th>
                    <th>Follow Up</th>
                    <th>Entered By</th>
                    <th>Date of Visit</th>
                    <th style={{ minWidth: 140 }}>Data Captured</th>
                    <th>Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {report.followups.map((row) => (
                    <tr key={row.id}>
                      <td>{row.f_number || "-"}</td>
                      <td>{row.beneficiary_name || "-"}</td>
                      <td>{row.type || "-"}</td>
                      <td>{row.user || "-"}</td>
                      <td>{formatDate(row.date_of_visit)}</td>
                      <td>
                        <ProgressBar now={row.data_captured} label={`${row.data_captured}%`} variant={captureVariant(row.data_captured)} />
                      </td>
                      <td className="small">{row.missing.length ? row.missing.join(", ") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </>
      )}

      <Modal show={pendingType !== null} onHide={() => setPendingType(null)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Pending Follow-ups
            {pendingType ? ` - ${report?.overdue.find((row) => row.type_id === pendingType)?.type || ""}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-2 mb-3 small">
            <Badge
              bg={pendingType === "" ? "success" : "light"}
              text={pendingType === "" ? undefined : "dark"}
              style={{ cursor: "pointer" }}
              onClick={() => setPendingType("")}
            >
              All: {summary?.total_pending ?? 0}
            </Badge>
            {report?.overdue.filter((row) => row.pending > 0).map((row) => (
              <Badge
                key={row.type_id}
                bg={pendingType === row.type_id ? "success" : "light"}
                text={pendingType === row.type_id ? undefined : "dark"}
                style={{ cursor: "pointer" }}
                onClick={() => setPendingType(row.type_id)}
              >
                {row.type}: {row.pending}
              </Badge>
            ))}
          </div>
          <p className="text-muted small">
            A follow-up is pending when its due date (counted from the rescue date) has passed and it has not been entered.
            Families with a &quot;Completed&quot; follow-up are not counted.
          </p>
          {pendingLoading ? (
            <div className="text-center"><Spinner animation="border" variant="success" /></div>
          ) : (
            <Table bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>FSF ID</th>
                  <th>Family</th>
                  <th>Mobile</th>
                  <th>Rescue Date</th>
                  <th>Follow Up</th>
                  <th>Due Date</th>
                  <th>Days Overdue</th>
                  <th>Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {pendingRows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted">No pending follow-ups.</td></tr>
                ) : (
                  pendingRows.map((row) => (
                    <tr key={`${row.person_id}-${row.type_id}`}>
                      <td>{row.f_number || "-"}</td>
                      <td>{row.beneficiary_name || "-"}</td>
                      <td>{row.mob || "-"}</td>
                      <td>{formatDate(row.rescue_date)}</td>
                      <td>{row.type}</td>
                      <td>{formatDate(row.due_date)}</td>
                      <td><Badge bg={row.days_overdue > 30 ? "danger" : "warning"}>{row.days_overdue}</Badge></td>
                      <td>{row.assigned_to || <span className="text-muted">Not distributed</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
